// The pieces a problem is told with: a card with its name, its priority and the elements it affects, and
// «Ver solución» opening everything else (why, how to fix it, where it is, the technical detail and the
// team's follow-up), and the filters of a list, folded away until they are worth the room. The same card is
// used for the whole scan and for one page.
import { ArrowRightIcon, ChevronDownIcon, InfoCircledIcon } from '@radix-ui/react-icons';
import { Link } from '@tanstack/react-router';
import { type ReactNode, useId, useMemo } from 'react';
import { Input, Label, SearchField, ToggleButton, ToggleButtonGroup } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import type { Device, PageIssue, Priority, ScanDetail, ScanIssue, ScannedPage } from '../api';
import { elementsOf } from '../element-logic';
import {
  categoryOf,
  FILTER_MINIMUM,
  type Filters,
  hasFilters,
  isFullyDiscarded,
  NO_FILTERS,
  type Problem,
} from '../results-logic';
import { Button, buttonClass, Fold } from '../ui';
import { pathOf } from '../util';
import { ToneIcon } from '../viz';
import { CodeBox } from './code-box';
import { BulkState, type Rights, StateForm } from './follow-state';
import { ScanProblemSolution } from './issue-solution';
import { GuideLink } from './manual-parts';
import { useDate } from './scan-parts';

export const PRIORITY_TONE = {
  alta: 'critica',
  media: 'moderada',
  baja: 'leve',
  pendiente: 'review',
} as const;

/** The priority in words (and an icon of its own shape): "Prioridad alta", never only a colour. */
export function PriorityChip({ priority }: { priority: Priority }) {
  const { t } = useTranslation();
  return (
    <span className="tone-chip" data-tone={PRIORITY_TONE[priority]}>
      <ToneIcon tone={PRIORITY_TONE[priority]} />
      {t(`results.priority.${priority}`)}
    </span>
  );
}

/** Where the finding comes from: found by the engine, or (soon) written down in a manual check. */
export function OriginChip({ review, manual }: { review?: boolean; manual?: boolean }) {
  const { t } = useTranslation();
  return (
    <span className="origin-chip">
      {t(manual ? 'results.origin.manual' : review ? 'results.origin.review' : 'results.origin.automatic')}
    </span>
  );
}

/**
 * What a priority means for this problem, and who it affects most. A best practice, SEO or quality item
 * has its own wording: it is not an accessibility problem and does not say it hinders people.
 */
export function PriorityWhy({ problem }: { problem: Problem }) {
  const { t } = useTranslation();
  const who = problem.affects.map((item) => item.label.toLocaleLowerCase()).join(', ');
  const category = categoryOf(problem);
  const text =
    category === 'seo' || category === 'quality'
      ? t(`results.priorityWhyOther.${problem.priority === 'pendiente' ? 'baja' : problem.priority}`)
      : category === 'bestPractices'
        ? t(`results.priorityWhyPractice.${problem.priority === 'pendiente' ? 'baja' : problem.priority}`)
        : t(`results.priorityWhy.${problem.priority}`);
  return (
    <p className="problem-why">
      <strong>{t('results.why')}</strong> {text}
      {who &&
        problem.module === 'accesibilidad' &&
        problem.result === 'violation' &&
        category !== 'bestPractices' && <> {t('results.affects', { who })}</>}
    </p>
  );
}

const deviceKey = (devices: Device[]) =>
  devices.length === 2
    ? 'both'
    : devices[0] === 'movil'
      ? 'mobile'
      : devices[0] === 'escritorio'
        ? 'desktop'
        : null;

const elementsText = (t: (key: string, vars?: Record<string, unknown>) => string, problem: Problem) =>
  t(
    `results.card.${problem.result === 'incomplete' ? 'review' : 'elements'}${problem.capped ? 'AtLeast' : ''}`,
    {
      count: problem.elements,
    },
  );

/**
 * A problem of the whole scan as one row, as in SiteNorma's WordPress plugin: its priority, its name and how many elements and
 * pages it affects; everything else opens from the row, in a fixed order (the problem, how to fix it, the WCAG reference,
 * the elements, the pages) with the technical detail and the team's follow-up apart, closed.
 */
function ScanIssueCard({
  problem,
  open,
  onToggle,
  rights,
  siteId,
  scanId,
  pages,
  scan,
  anchor,
  headingLevel,
}: {
  /** The analysis the problem is of: with it, an opened problem shows the same solution as in a page. */
  scan?: ScanDetail;
  problem: Problem;
  open: boolean;
  onToggle: () => void;
  rights: Rights;
  siteId: string;
  scanId: string;
  pages: ScannedPage[];
  anchor?: string;
  headingLevel: 3 | 4;
}) {
  const { t } = useTranslation();
  const panel = useId();
  const Heading = `h${headingLevel}` as const;
  const review = problem.result === 'incomplete';
  const manual = problem.origin === 'manual';
  return (
    <li
      className="rule-card issue-card"
      data-tone={PRIORITY_TONE[problem.priority]}
      data-open={open || undefined}
      id={anchor}
    >
      <Heading className="issue-heading">
        <button
          type="button"
          className="issue-toggle"
          aria-expanded={open}
          aria-controls={panel}
          onClick={onToggle}
        >
          <PriorityChip priority={problem.priority} />
          <span className="issue-name">{problem.title}</span>
          <span className="issue-counts">
            {/* Same units as the WordPress plugin and AffectedLine: elements and pages together. */}
            {problem.elements > 0 && <span>{elementsText(t, problem)}</span>}
            <span>{t('results.pagesCount', { count: problem.pages })}</span>
          </span>
          {problem.global && (
            <span className="origin-chip origin-chip-global">{t('results.shared.chip')}</span>
          )}
          {manual && <OriginChip manual />}
          <ChevronDownIcon className="issue-chevron" aria-hidden="true" />
        </button>
      </Heading>
      {isFullyDiscarded(problem) && <p className="issue-note text-sm">{t('follow.problem.allDiscarded')}</p>}
      <div id={panel} hidden={!open} className="issue-body">
        {open && manual && <ManualProblemBody problem={problem} siteId={siteId} scanId={scanId} />}
        {open && !manual && (
          <IssueDetail
            problem={problem}
            review={review}
            rights={rights}
            siteId={siteId}
            scanId={scanId}
            pages={pages}
            scan={scan}
          />
        )}
      </div>
    </li>
  );
}

/**
 * What opens from a problem row. With the analysis at hand it is the same solution as in «Páginas», in the same order
 * (see ScanProblemSolution); without it, the plain parts of the problem.
 */
function IssueDetail({
  problem,
  review,
  rights,
  siteId,
  scanId,
  pages,
  scan,
}: {
  problem: Problem;
  review: boolean;
  rights: Rights;
  siteId: string;
  scanId: string;
  pages: ScannedPage[];
  scan?: ScanDetail;
}) {
  const { t } = useTranslation();
  if (scan) return <ScanProblemSolution problem={problem} scan={scan} siteId={siteId} rights={rights} />;
  return (
    <div className="issue-sections">
      {problem.description && (
        <section>
          <h5>{t('results.issue.problem')}</h5>
          <p>{problem.description}</p>
        </section>
      )}
      {problem.fix && (
        <section>
          <h5>{t(review ? 'results.issue.check' : 'results.issue.fix')}</h5>
          <p>{problem.fix}</p>
        </section>
      )}
      {problem.criteriaDetail.length > 0 && (
        <section>
          <h5>{t('results.issue.wcag')}</h5>
          <ul>
            {problem.criteriaDetail.map((criterion) => (
              <li key={criterion.id}>
                {t('results.detail.criterion', {
                  id: criterion.id,
                  title: criterion.title,
                  level: criterion.level ?? '',
                })}
              </li>
            ))}
          </ul>
        </section>
      )}
      <AffectedElements problem={problem} pages={pages} scanId={scanId} siteId={siteId} />
      <AffectedPages problem={problem} pages={pages} scanId={scanId} siteId={siteId} />
      <Fold title={t('results.issue.technical')}>
        <IssueTechnical problem={problem} />
      </Fold>
      <ProblemTracking problem={problem} rights={rights} siteId={siteId} scanId={scanId} />
    </div>
  );
}

/** Examples of the elements where the problem was found, each leading to its page with the element marked. */
function AffectedElements({
  problem,
  pages,
  scanId,
  siteId,
}: {
  problem: Problem;
  pages: ScannedPage[];
  scanId: string;
  siteId: string;
}) {
  const { t } = useTranslation();
  const byKey = new Map(pages.map((page) => [page.key, page]));
  const seen = new Set<string>();
  const examples = (problem.issues as ScanIssue[])
    .flatMap((issue) => issue.examples)
    .filter((example) => {
      const key = `${example.pageKey}|${example.selector}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 5);
  if (examples.length === 0) return null;
  const more = Math.max(0, problem.elements - examples.length);
  return (
    <section>
      <h5>{t('results.issue.elements')}</h5>
      <ul className="issue-elements">
        {examples.map((example) => {
          const page = byKey.get(example.pageKey);
          return (
            <li key={example.id}>
              <Link
                to="/sites/$siteId/paginas"
                params={{ siteId }}
                search={{ scan: scanId, p: example.pageKey, e: example.id, k: problem.key }}
                className="wrap-anywhere"
              >
                <strong>{example.element.title}</strong>
                {example.element.zoneLabel && <span> — {example.element.zoneLabel}</span>}
                <span className="sr-only"> — {t('results.issue.seeInPage')}</span>
              </Link>
              {page && <span className="text-ink-muted"> · {page.title || pathOf(page.finalUrl)}</span>}
            </li>
          );
        })}
      </ul>
      {more > 0 && (
        <p className="text-sm text-ink-muted">{t('results.issue.moreElements', { count: more })}</p>
      )}
    </section>
  );
}

/**
 * The pages a problem is on. When it is a shared component it says it once, in a sentence, and the pages (with their
 * language and what the team did on each) are one fold away: the selectors are in the technical detail.
 */
function AffectedPages({
  problem,
  pages,
  scanId,
  siteId,
}: {
  problem: Problem;
  pages: ScannedPage[];
  scanId: string;
  siteId: string;
}) {
  const { t } = useTranslation();
  const byKey = new Map(pages.map((page) => [page.key, page]));
  const sharedPages = Math.max(0, ...problem.shared.map((component) => component.pageKeys.length));
  const owner = problem.shared.find((component) => component.ownerKey && component.ownerKind === 'home');
  const ownerPage = owner?.ownerKey ? byKey.get(owner.ownerKey) : undefined;
  return (
    <section>
      <h5>{t('results.issue.pages')}</h5>
      {problem.shared.length > 0 && (
        <p>
          {problem.shared.length === 1
            ? t('results.issue.sharedOne', { pages: sharedPages })
            : t('results.issue.sharedMany', { pages: sharedPages, components: problem.shared.length })}
          {ownerPage && owner?.ownerKey && (
            <>
              {' '}
              <Link
                to="/sites/$siteId/paginas"
                params={{ siteId }}
                search={{ scan: scanId, p: owner.ownerKey, k: problem.key }}
                className="wrap-anywhere"
              >
                {t('results.shared.owner', { page: ownerPage.title || pathOf(ownerPage.finalUrl) })}
              </Link>
            </>
          )}
        </p>
      )}
      <Fold light title={t('results.issue.seePages', { count: problem.pages })}>
        <WhereList problem={problem} pages={pages} scanId={scanId} siteId={siteId} bare />
      </Fold>
    </section>
  );
}

/** The technical record of a problem: how it is counted, and the code of some of its elements on dark boxes. */
function IssueTechnical({ problem }: { problem: Problem }) {
  const { t } = useTranslation();
  // The same code on several pages (a shared component) is shown once.
  const seen = new Set<string>();
  const examples = (problem.issues as ScanIssue[])
    .flatMap((issue) => issue.examples)
    .filter((example) => {
      const key = `${example.selector}|${example.html}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 3);
  return (
    <div className="issue-technical">
      <PriorityWhy problem={problem} />
      <TechnicalDetail problem={problem} scope="scan" bare />
      {examples.map((example) => (
        <div key={example.id} className="tech-code">
          <p className="font-bold">{example.element.title}</p>
          <CodeBox code={example.selector} label={t('element.technical.selector')} />
          {example.xpath && <CodeBox code={example.xpath} label={t('element.technical.xpath')} />}
          <CodeBox code={example.html} label={t('element.technical.code')} />
        </div>
      ))}
    </div>
  );
}

/**
 * The team's follow-up of a problem, apart from the instructions: the work, the validation of the detections and the
 * verification of the corrections, told separately, and the way to apply a work status to all its elements.
 */
function ProblemTracking({
  problem,
  rights,
  siteId,
  scanId,
}: {
  problem: Problem;
  rights: Rights;
  siteId: string;
  scanId: string;
}) {
  const { t } = useTranslation();
  const { follow } = problem;
  if (!rights.track && follow.tracked === 0) return null;
  const work = (['pendiente', 'en-curso', 'aplicada', 'aplazada', 'archivada'] as const)
    .filter((status) => follow.work[status] > 0)
    .map((status) => `${t(`follow.work.statuses.${status}`)}: ${follow.work[status]}`)
    .join(' · ');
  return (
    <Fold
      title={t('results.issue.tracking.title')}
      hint={
        follow.tracked > 0
          ? t('follow.problem.counts', { count: follow.tracked, total: problem.elements + problem.discarded })
          : t('results.issue.tracking.hint')
      }
    >
      <dl className="tracking-lines">
        <div>
          <dt>{t('follow.quick.work')}</dt>
          <dd>{work || t('results.issue.tracking.none')}</dd>
        </div>
        <div>
          <dt>{t('follow.validation.title')}</dt>
          <dd>
            {follow.confirmed + problem.discarded > 0
              ? t('results.issue.tracking.validation', {
                  confirmed: follow.confirmed,
                  discarded: problem.discarded,
                })
              : t('results.issue.tracking.noDecisions')}
          </dd>
        </div>
        <div>
          <dt>{t('results.issue.tracking.verificationTitle')}</dt>
          <dd>
            {follow.tracked > 0
              ? t('results.issue.tracking.verification', {
                  verified: follow.check.verified,
                  persists: follow.check.persists,
                  pending: follow.check.pending,
                })
              : t('follow.panel.kinds.sin-comprobar')}
          </dd>
        </div>
      </dl>
      <BulkState
        scanId={scanId}
        siteId={siteId}
        target={{ issueIds: problem.issues.map((issue) => issue.id) }}
        count={problem.elements}
        mayTrack={rights.track}
      />
    </Fold>
  );
}

/**
 * What a problem affects, in the units of the summary: distinct elements (desktop and mobile together),
 * with a "+" when the engine stopped counting. For the whole scan it says in how many pages.
 */
export function AffectedLine({ problem, scope }: { problem: Problem; scope: 'scan' | 'page' }) {
  const { t } = useTranslation();
  const review = problem.result === 'incomplete';
  const pages = t('results.pagesCount', { count: problem.pages });
  if (problem.elements === 0)
    return (
      <span>
        {scope === 'page' ? t('results.counts.pageOnly') : t('results.counts.pagesOnly', { pages })}
      </span>
    );
  const elements = t(`results.card.${review ? 'review' : 'elements'}${problem.capped ? 'AtLeast' : ''}`, {
    count: problem.elements,
  });
  // Several language versions: how many, so a problem repeated in each is not read as separate problems.
  const languages = problem.languages.codes;
  const text =
    scope === 'scan'
      ? [
          elements,
          pages,
          languages.length > 1 &&
            t('results.counts.languages', { count: languages.length, list: languages.join(', ') }),
        ]
          .filter(Boolean)
          .join(' · ')
      : elements;
  return <span>{text}</span>;
}

/** Where the problem is, page by page, with the way to see it marked on the page. */
function WhereList({
  problem,
  pages,
  scanId,
  siteId,
  bare = false,
}: {
  problem: Problem;
  pages: ScannedPage[];
  scanId: string;
  siteId: string;
  /** Inside a fold that already says what it is: no heading of its own. */
  bare?: boolean;
}) {
  const { t } = useTranslation();
  const stats = new Map<string, { elements: number; devices: Device[]; capped: boolean }>();
  for (const issue of problem.issues as ScanIssue[]) {
    for (const [key, stat] of Object.entries(issue.pageStats)) {
      const before = stats.get(key);
      stats.set(key, {
        elements: (before?.elements ?? 0) + stat.elements,
        devices: [...new Set([...(before?.devices ?? []), ...stat.devices])] as Device[],
        capped: Boolean(before?.capped) || stat.capped,
      });
    }
  }
  // The first example found on each page: where "see it on the page" opens.
  const firstElement = new Map<string, string>();
  for (const issue of problem.issues as ScanIssue[])
    for (const example of issue.examples)
      if (!firstElement.has(example.pageKey)) firstElement.set(example.pageKey, example.id);
  // What the team follows of the problem on each page: each page is settled on its own.
  const follow = new Map<string, { tracked: number; work: Record<string, number> }>();
  for (const issue of problem.issues as ScanIssue[])
    for (const [key, here] of Object.entries(issue.pageFollow ?? {})) {
      const before = follow.get(key) ?? { tracked: 0, work: {} };
      before.tracked += here.tracked;
      for (const [status, count] of Object.entries(here.work))
        before.work[status] = (before.work[status] ?? 0) + count;
      follow.set(key, before);
    }
  const rows = pages
    .filter((page) => stats.has(page.key))
    .map((page) => ({ page, stat: stats.get(page.key)! }))
    .sort((a, b) => b.stat.elements - a.stat.elements);
  if (rows.length === 0) return null;
  return (
    <section aria-label={t('results.detail.where')} className="problem-where">
      {!bare && <h5 className="font-bold">{t('results.detail.where')}</h5>}
      <ul>
        {rows.map(({ page, stat }) => {
          const device = deviceKey(stat.devices);
          return (
            <li key={page.key}>
              <Link
                to="/sites/$siteId/paginas"
                params={{ siteId }}
                search={{ scan: scanId, p: page.key, e: firstElement.get(page.key) }}
                className="wrap-anywhere font-bold"
              >
                {page.title || pathOf(page.finalUrl)}
                <span className="sr-only"> ({pathOf(page.finalUrl)})</span>
              </Link>
              <span className="text-ink-muted">
                {' '}
                ·{' '}
                {stat.elements > 0
                  ? t(`results.counts.elementsOnly${stat.capped ? 'AtLeast' : ''}`, { count: stat.elements })
                  : t('results.counts.affectsPage')}
                {device && <> · {t(`results.devices.${device}`)}</>}
                {page.lang && <> · {page.lang}</>}
                {follow.get(page.key) && (
                  <>
                    {' '}
                    ·{' '}
                    {Object.entries(follow.get(page.key)?.work ?? {})
                      .map(([status, count]) => `${t(`follow.work.statuses.${status}`)} (${count})`)
                      .join(', ')}
                  </>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** The technical record of a problem: its name for the engine, the WCAG criteria and how its numbers are made. */
function TechnicalDetail({
  problem,
  scope,
  bare = false,
}: {
  problem: Problem;
  scope: 'scan' | 'page';
  /** Inside a fold that already says what it is. */
  bare?: boolean;
}) {
  const { t } = useTranslation();
  const groups = problem.issues.length;
  const Box = bare ? 'div' : 'details';
  return (
    <Box className={bare ? undefined : 'issue-details'}>
      {!bare && <summary className="cursor-pointer font-bold">{t('results.detail.technical')}</summary>}
      <dl className="problem-technical">
        <div>
          <dt>{t('results.detail.rule')}</dt>
          <dd>
            <code>{problem.ruleId}</code>
          </dd>
        </div>
        {problem.criteriaDetail.length > 0 && (
          <div>
            <dt>{t('results.detail.criteria')}</dt>
            <dd>
              <ul>
                {problem.criteriaDetail.map((criterion) => (
                  <li key={criterion.id}>
                    {t('results.detail.criterion', {
                      id: criterion.id,
                      title: criterion.title,
                      level: criterion.level ?? '',
                    })}
                  </li>
                ))}
              </ul>
            </dd>
          </div>
        )}
        <div>
          <dt>{t('results.detail.appearances')}</dt>
          <dd>
            {t('results.detail.appearancesValue', { count: problem.appearances })}{' '}
            <span className="text-ink-muted">{t('results.detail.appearancesHelp')}</span>
          </dd>
        </div>
        {groups > 1 && (
          <div>
            <dt>{t('results.detail.groups')}</dt>
            <dd>
              {t('results.detail.groupsValue', { count: groups })}{' '}
              <span className="text-ink-muted">{t('results.detail.groupsHelp')}</span>
            </dd>
          </div>
        )}
        {scope === 'scan' && (
          <div>
            <dt>{t('results.detail.scopeNote')}</dt>
            <dd className="text-ink-muted">{t('results.detail.scopeNoteValue')}</dd>
          </div>
        )}
      </dl>
    </Box>
  );
}

/** What a problem found in a manual review says: what the person found, where, under which conditions, and the way to its guide. */
function ManualProblemBody({
  problem,
  siteId,
  scanId,
}: {
  problem: Problem;
  siteId: string;
  scanId: string;
}) {
  const { t } = useTranslation();
  const date = useDate();
  return (
    <div className="manual-found">
      <p className="text-sm">{t('manual.found.note')}</p>
      <ul className="follow-list">
        {(problem.issues as ScanIssue[]).map((issue) => {
          const found = issue.manual;
          if (!found) return null;
          const conditions = [
            ...(found.conditions.ids ?? []).map((id) => t(`manual.condition.${id}`)),
            ...(found.conditions.notes ? [found.conditions.notes] : []),
          ].join(' · ');
          return (
            <li key={issue.id} className="follow-item">
              <p className="font-bold">{t('manual.found.from', { id: found.criterion })}</p>
              {issue.description && <p className="whitespace-pre-line">{issue.description}</p>}
              <ul className="manual-list-plain text-sm">
                {issue.pages[0] && <li className="wrap-anywhere">{issue.pages[0]}</li>}
                {found.element && <li>{t('manual.found.element', { element: found.element })}</li>}
                <li>{t('manual.found.device', { device: t(`manual.form.devices.${found.device}`) })}</li>
                {conditions && <li>{t('manual.found.conditions', { list: conditions })}</li>}
                {found.evidence && (
                  <li className="wrap-anywhere">{t('manual.found.evidence', { text: found.evidence })}</li>
                )}
                {found.at && (
                  <li>
                    {t('manual.found.by', {
                      name: found.by?.name || t('manual.row.someone'),
                      date: date(found.at, 'short'),
                    })}
                  </li>
                )}
              </ul>
              {found.needsNew && (
                <p className="problem-chips">
                  <span className="origin-chip manual-needs-new">{t('manual.needsNew')}</span>{' '}
                  {t('manual.needsNewHint')}
                </p>
              )}
              {found.guide && (
                <div>
                  <GuideLink siteId={siteId} criterion={found.criterion} search={{ scan: scanId }}>
                    {t('manual.found.open')}
                  </GuideLink>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** What a problem card needs to open its elements on the capture of a page. */
export interface PageContext {
  scan: ScanDetail;
  page: ScannedPage;
  /** The device on screen. */
  device: Device;
  /** The element opened, by the id of any of its occurrences. */
  selectedId: string | undefined;
  onSelect: (id: string | null) => void;
  /** How tall each device's capture is, once loaded. */
  heights: Partial<Record<Device, number>>;
  /** The problem whose panel is open (by its key), if any: then the panel takes the place of the list. */
  problemKey: string | undefined;
  /** Opens the panel of a problem, and its first element on the capture; null goes back to the list. */
  onOpenProblem: (problem: Problem<PageIssue> | null) => void;
  /** The capture is not beside the problems (a narrow screen): the panel leads to it with a button. */
  stacked: boolean;
  onShowCapture: () => void;
}

/**
 * A problem as a card: its plain name, its priority and the elements it affects, with «Ver solución»
 * opening everything else (what it is, why it matters, how to fix it). On a page, opening it marks its
 * elements on the screenshot.
 */
export function ProblemCard({
  problem,
  scope,
  open,
  onToggle,
  rights,
  siteId,
  scanId,
  pages,
  scan,
  headingLevel = 4,
  pageContext,
  anchor,
}: {
  /** The analysis (in a whole analysis): an opened problem shows the same solution as in a page. */
  scan?: ScanDetail;
  problem: Problem;
  scope: 'scan' | 'page';
  open: boolean;
  onToggle: () => void;
  rights: Rights;
  siteId: string;
  scanId: string;
  pages: ScannedPage[];
  headingLevel?: 3 | 4;
  /** On a page: opens the problem's elements one by one on the capture. */
  pageContext?: PageContext;
  /** The id the "start here" list links to. */
  anchor?: string;
}) {
  const { t } = useTranslation();
  const panel = useId();
  const Heading = `h${headingLevel}` as const;
  const review = problem.result === 'incomplete';
  if (scope === 'scan')
    return (
      <ScanIssueCard
        problem={problem}
        open={open}
        onToggle={onToggle}
        rights={rights}
        siteId={siteId}
        scanId={scanId}
        pages={pages}
        scan={scan}
        anchor={anchor}
        headingLevel={headingLevel}
      />
    );
  // On a page the solution has a panel of its own beside the capture; elsewhere the card opens in place.
  const panelled = scope === 'page' && pageContext !== undefined && problem.origin !== 'manual';
  return (
    <li
      className={`rule-card problem-card${panelled ? ' problem-card-click' : ''}`}
      data-tone={PRIORITY_TONE[problem.priority]}
      id={anchor}
    >
      <div className="problem-head">
        <Heading className="problem-title">
          {panelled ? (
            // The name is the control and stretches over the whole card: one click anywhere opens the solution.
            <button
              type="button"
              className="problem-card-open"
              onClick={() => pageContext?.onOpenProblem(problem as Problem<PageIssue>)}
            >
              {problem.title}
              <span className="sr-only">
                {' '}
                — {t(review || problem.origin === 'manual' ? 'results.actions.check' : 'results.actions.fix')}
              </span>
            </button>
          ) : (
            problem.title
          )}
        </Heading>
        <p className="problem-meta">
          <PriorityChip priority={problem.priority} />
          <span aria-hidden="true">·</span>
          <AffectedLine problem={problem} scope={scope} />
          {problem.global && (
            <span className="origin-chip origin-chip-global">{t('results.shared.chip')}</span>
          )}
          {problem.global && (
            <span className="text-sm text-ink-muted">
              {t('results.shared.elementIn', {
                count: Math.max(...problem.shared.map((component) => component.pageKeys.length)),
              })}
            </span>
          )}
          {problem.origin === 'manual' && <OriginChip manual />}
          {(['en-curso', 'aplicada', 'aplazada', 'archivada'] as const)
            .filter((status) => problem.follow.work[status] > 0)
            .map((status) => (
              <span key={status} className="origin-chip">
                {t(`follow.work.statuses.${status}`)} · {problem.follow.work[status]}
              </span>
            ))}
          {problem.discarded > 0 && (
            <span className="origin-chip">
              {t('follow.problem.discardedElements', { count: problem.discarded })}
            </span>
          )}
        </p>
        {isFullyDiscarded(problem) && <p className="text-sm">{t('follow.problem.allDiscarded')}</p>}
      </div>
      {panelled ? (
        <div className="problem-actions" aria-hidden="true">
          <ArrowRightIcon className="problem-arrow" />
        </div>
      ) : (
        <div className="problem-actions">
          <Button
            variant={open && !panelled ? 'secondary' : 'primary'}
            aria-expanded={panelled ? undefined : open}
            aria-controls={panelled ? undefined : panel}
            onPress={() =>
              panelled ? pageContext?.onOpenProblem(problem as Problem<PageIssue>) : onToggle()
            }
          >
            {t(review || problem.origin === 'manual' ? 'results.actions.check' : 'results.actions.fix')}
            <span className="sr-only"> — {problem.title}</span>
            <ArrowRightIcon
              aria-hidden="true"
              className="rule-chevron"
              data-open={(open && !panelled) || undefined}
            />
          </Button>
        </div>
      )}
      <div id={panel} hidden={!open || panelled} className="rule-body">
        {open && problem.origin === 'manual' && (
          <ManualProblemBody problem={problem} siteId={siteId} scanId={scanId} />
        )}
        {open && problem.origin !== 'manual' && (
          <>
            <div className="problem-about">
              {problem.description && <p className="problem-explain">{problem.description}</p>}
              <PriorityWhy problem={problem} />
            </div>
            {problem.fix && (
              <div className="rule-fix">
                <p className="font-bold">{t(review ? 'results.detail.howToCheck' : 'scan.fix')}</p>
                <p>{problem.fix}</p>
              </div>
            )}
            <TechnicalDetail problem={problem} scope={scope} />
            <ProblemFollowUp
              problem={problem}
              rights={rights}
              siteId={siteId}
              scanId={scanId}
              occurrenceIds={[]}
            />
          </>
        )}
      </div>
    </li>
  );
}

/**
 * What the team follows about a problem: each element has its own state, so this says how many are
 * followed and offers to apply one state to all of them at once (saved separately on each).
 */
export function ProblemFollowUp({
  problem,
  rights,
  siteId,
  scanId,
  occurrenceIds,
  compact = false,
}: {
  problem: Problem;
  rights: Rights;
  siteId: string;
  scanId: string;
  /** On a page: the elements there. Empty for the whole scan, where the problem's issues are the target. */
  occurrenceIds: string[];
  /** Inside the advanced options of an element, which already say what they are: no title. */
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const { follow } = problem;
  const target =
    occurrenceIds.length > 0 ? { occurrenceIds } : { issueIds: problem.issues.map((issue) => issue.id) };
  const total = problem.elements;
  if (!rights.track && follow.tracked === 0) return null;
  return (
    <section className="problem-followup" aria-label={t('follow.problem.title')}>
      {!compact && <h5 className="font-bold">{t('follow.problem.title')}</h5>}
      {/* Only what there is: how many elements the team follows, when it follows any. */}
      {follow.tracked > 0 && (
        <p className="text-sm">
          {t('follow.problem.counts', { count: follow.tracked, total: total + problem.discarded })}
        </p>
      )}
      {rights.track && total > 0 && (
        <details className="issue-triage">
          <summary className="cursor-pointer font-bold">{t('follow.problem.bulkTitle')}</summary>
          <p className="text-sm text-ink-muted">{t('follow.problem.bulkHint')}</p>
          <StateForm
            scanId={scanId}
            siteId={siteId}
            target={target}
            state={null}
            result={problem.result}
            count={occurrenceIds.length > 0 ? new Set(occurrenceIds).size : total}
            mayTrack={rights.track}
            mayDiscard={rights.discard}
          />
        </details>
      )}
    </section>
  );
}

/**
 * The filters of a list: folded under «Filtrar», and only offered when the list is long enough to need them.
 * They narrow the list and never change the figures of the summary or inside a problem; when they are on,
 * it says how many items are shown and offers to remove them all.
 */
export function FilterBar({
  filters,
  onChange,
  shown,
  total,
  counts,
  withPriority = true,
  inline = false,
  areas = false,
}: {
  filters: Filters;
  onChange: (filters: Filters) => void;
  shown: number;
  total: number;
  /** How many items each priority has, unfiltered, so the chips say what they would show. */
  counts: Partial<Record<Priority, number>>;
  withPriority?: boolean;
  /** Always in view, as in the WordPress plugin (the list of a whole scan), instead of folded under «Filtrar». */
  inline?: boolean;
  /** Offers the area (best practices, SEO, quality): for the results that are not accessibility problems. */
  areas?: boolean;
}) {
  const { t } = useTranslation();
  const area = filters.area ?? 'todas';
  const active =
    filters.priorities.length +
    (filters.device !== 'todos' ? 1 : 0) +
    (filters.query.trim() ? 1 : 0) +
    (area !== 'todas' ? 1 : 0);
  // A short list needs no filters; but filters that are on stay in reach so they can be removed.
  if (!inline && total < FILTER_MINIMUM && !hasFilters(filters)) return null;
  if (inline && total === 0 && !hasFilters(filters)) return null;
  const controls = (
    <div className="issue-filters">
      {withPriority && (
        <ToggleButtonGroup
          aria-label={t('results.filters.priority')}
          selectionMode="multiple"
          selectedKeys={filters.priorities}
          onSelectionChange={(keys) => onChange({ ...filters, priorities: [...keys] as Priority[] })}
          className="filter-chips"
        >
          {(['alta', 'media', 'baja'] as const).map((priority) => (
            <ToggleButton
              key={priority}
              id={priority}
              className="filter-chip"
              data-tone={PRIORITY_TONE[priority]}
            >
              {t(`results.priority.${priority}`)}
              <span className="filter-count">{counts[priority] ?? 0}</span>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}
      {areas && (
        <ToggleButtonGroup
          aria-label={t('results.issue.area')}
          selectionMode="single"
          disallowEmptySelection
          selectedKeys={[area]}
          onSelectionChange={(keys) =>
            onChange({ ...filters, area: ([...keys][0] ?? 'todas') as Filters['area'] })
          }
          className="filter-chips"
        >
          {(['todas', 'bestPractices', 'seo', 'quality'] as const).map((value) => (
            <ToggleButton key={value} id={value} className="filter-chip">
              {t(`results.issue.areas.${value}`)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}
      <ToggleButtonGroup
        aria-label={t('results.filters.device')}
        selectionMode="single"
        disallowEmptySelection
        selectedKeys={[filters.device]}
        onSelectionChange={(keys) =>
          onChange({ ...filters, device: ([...keys][0] ?? 'todos') as Filters['device'] })
        }
        className="filter-chips"
      >
        {(['todos', 'escritorio', 'movil'] as const).map((device) => (
          <ToggleButton key={device} id={device} className="filter-chip">
            {t(`results.filters.devices.${device}`)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <SearchField
        value={filters.query}
        onChange={(query) => onChange({ ...filters, query })}
        className="filter-search"
      >
        <Label>{t('results.filters.search')}</Label>
        <Input className="min-h-11 rounded-md border border-field-border bg-paper px-3 py-2 text-ink" />
      </SearchField>
    </div>
  );
  return (
    <div className="results-filters" data-inline={inline || undefined}>
      {inline ? (
        controls
      ) : (
        <details className="filter-menu" open={active > 0 || undefined}>
          <summary>
            {t('results.filters.title')}
            {active > 0 && <span className="filter-count">{active}</span>}
          </summary>
          {controls}
        </details>
      )}
      {/* How many results meet what is selected, always said (the list changes in place, nothing reloads). */}
      {(inline || hasFilters(filters)) && (
        <div className="filter-result">
          <p role="status">
            {t(hasFilters(filters) ? 'results.filters.shown' : 'results.issue.showing', { shown, total })}
          </p>
          {hasFilters(filters) && (
            <Button variant="secondary" onPress={() => onChange(NO_FILTERS)}>
              {t('results.filters.clear')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/** A short note with an information icon: what a section is and is not. */
export function Note({ children }: { children: ReactNode }) {
  return (
    <p className="results-note">
      <InfoCircledIcon aria-hidden="true" />
      <span>{children}</span>
    </p>
  );
}

export { buttonClass };
